import { createSelector } from '@ngrx/store';
import { CostBasisPosition } from '@store/reducers/cost-basis/cost-basis.reducer';
import { GiftingRecipient, GiftingState } from '@store/reducers/gifting/gifting.reducer';
import { TriageState } from '@store/reducers/triage/triage.reducer';
import { selectSelectedPositions } from '@store/selectors/cost-basis/cost-basis.selectors';
import {
  selectGiftingRecipient,
  selectGiftingState,
} from '@store/selectors/gifting/gifting.selectors';
import { selectedAccountsFrom } from '@store/selectors/selected-client-accounts/selected-client-accounts.selectors';
import { selectTriageState } from '@store/selectors/triage/triage.selectors';

import {
  buildAccountFromGifting,
  buildAccountToGifting,
  buildAdditionalTaxLot,
  buildGiftReceipt,
  isDollarAmountSpecIdFlow,
} from './cashier-gifting';

export const selectAccountFromGiftingReceipt = (editable: boolean = true) =>
  createSelector(
    selectTriageState,
    selectedAccountsFrom,
    (triageState: TriageState, accountsFrom) =>
      buildAccountFromGifting(triageState, accountsFrom[0], editable),
  );

export const selectAccountToGiftingReceipt = (editable: boolean = true) =>
  createSelector(
    selectGiftingRecipient,
    selectTriageState,
    (recipient: GiftingRecipient, triageState: TriageState) =>
      buildAccountToGifting(recipient, triageState, editable),
  );

export const selectGiftReceipt = (editable: boolean = true) =>
  createSelector(
    selectSelectedPositions,
    selectTriageState,
    selectGiftingState,
    (
      selectedPositions: CostBasisPosition[],
      triageState: TriageState,
      giftingState: GiftingState,
    ) => buildGiftReceipt(selectedPositions, triageState, giftingState, editable),
  );

export const selectAdditionalTaxLotReceipt = (editable: boolean = true) =>
  createSelector(
    selectSelectedPositions,
    selectTriageState,
    (selectedPositions: CostBasisPosition[], triageState: TriageState) =>
      buildAdditionalTaxLot(selectedPositions, triageState, editable),
  );

export const selectIsDollarAmountSpecIdFlow = createSelector(
  selectSelectedPositions,
  (positions: CostBasisPosition[]) =>
    positions?.some((pos) => {
      return isDollarAmountSpecIdFlow(pos);
    }),
);
export const selectGiftingReceiptData = (editable: boolean = true) =>
  createSelector(
    selectAccountFromGiftingReceipt(editable),
    selectGiftReceipt(editable),
    selectAdditionalTaxLotReceipt(editable),
    selectAccountToGiftingReceipt(editable),
    (from, gift, additionalTaxLots, to) => {
      return {
        from,
        gift,
        additionalTaxLots,
        to,
      };
    },
  );
